import {NOOP_FUNCTION} from "../../stem-core/base/Utils";
import {RemoveHandle} from "../../stem-core/base/Dispatcher";

// A held scroll lock. A RemoveHandle so holders can hand it straight to addCleanupJob. scrollbarWidth is
// measured when the lock is taken, for callers offsetting fixed elements so they don't jump.
export interface ScrollLockHandle extends RemoveHandle {
    scrollbarWidth: number;
}

export function removeScrollOnScrim(): ScrollLockHandle {
    const element = document.documentElement.clientHeight !== document.documentElement.scrollHeight ? document.documentElement : document.body;
    const scrollbarWidth = window.innerWidth - element.clientWidth;
    const computedStyle = getComputedStyle(element);

    if (computedStyle.getPropertyValue("overflow").endsWith("hidden")) {
        // The page already blocks scroll; don't touch it.
        return {
            scrollbarWidth,
            remove: NOOP_FUNCTION,
        };
    }

    // Snapshot the inline styles, not the computed ones: writing a computed value back would pin e.g.
    // "overflow: visible" inline and fight the page's own CSS.
    const inlineOverflow = element.style.overflow;
    const inlinePaddingRight = element.style.paddingRight;
    const initialRightPadding = computedStyle.getPropertyValue("padding-right");

    Object.assign(element.style, {
        overflow: "hidden",
        paddingRight: `${parseFloat(initialRightPadding) + scrollbarWidth}px`,
    });

    return {
        scrollbarWidth,
        remove: () => {
            element.style.overflow = inlineOverflow;
            element.style.paddingRight = inlinePaddingRight;
        },
    };
}

// One refcounted lock shared by every scrim source (the wallet/popup flow and the gated-content gates), so
// nested locks can't write overflow:hidden twice. Every holder releases its own lock on teardown, and a gate
// the merchant's router discards tears itself down (see GateElement), so no lock outlives its owner.
class BodyScrollLock {
    private underlyingLock: ScrollLockHandle | null = null;
    private holders = new Set<ScrollLockHandle>();

    acquire(): ScrollLockHandle {
        if (!this.underlyingLock) {
            this.underlyingLock = removeScrollOnScrim();
        }
        const holder: ScrollLockHandle = {
            scrollbarWidth: this.underlyingLock.scrollbarWidth,
            remove: () => this.releaseHolder(holder),
        };
        this.holders.add(holder);
        return holder;
    }

    private releaseHolder(holder: ScrollLockHandle) {
        if (!this.holders.delete(holder)) {
            return; // Idempotent, so a double release can't drop the count below the live holders.
        }
        if (this.holders.size === 0) {
            this.underlyingLock?.remove();
            this.underlyingLock = null;
        }
    }
}

export const bodyScrollLock = new BodyScrollLock();
